<template>
  <div id="appointment">
    <div class="container">
      <div class="section-title">
        <h2 class="text-white">Make an Appointment</h2>
        <p class="text-white">
          Please fill in the details below and submit to have our dedicated
          concierge <br />set you up with an appointment at your convenience
        </p>
      </div>

      <form @submit.prevent="sendMail" novalidate>
        <div class="form-row">
          <div class="col-lg-3 col-12 tab-responsive">
            <input
              ref="patientName"
              v-model.trim="patientName"
              type="text"
              class="form-control form-box"
              placeholder="Name"
            />
          </div>

          <div class="col-lg-3 col-12 tab-responsive">
            <input
              ref="patientEmail"
              v-model.trim="patientEmail"
              type="email"
              class="form-control form-box"
              placeholder="E-mail"
            />
          </div>

          <div class="col-lg-3 col-12 tab-responsive">
            <input
              ref="patientContactNumber"
              v-model.number="patientContactNumber"
              type="tel"
              class="form-control form-box"
              placeholder="Phone"
            />
          </div>

          <div class="col-lg-3 col-12">
            <input
              ref="opNumber"
              v-model.number="opNumber"
              type="text"
              class="form-control form-box"
              placeholder="OP Number (If any)"
            />
          </div>
        </div>

        <div class="form-row mt-3">
          <div class="col-lg-4 col-12">
            <select
              v-model="selectedDeptId"
              id="inputState"
              class="form-control form-box"
              ref="selectedDeptId"
            >
              <option value="">Select a department</option>
              <option
                v-for="{ sys: { id }, departments } in departmentsList"
                :key="id"
                :value="id"
                >{{ departments }}</option
              >
            </select>
          </div>

          <div class="col-lg-4 col-12 tab-responsive">
            <select
              :disabled="!selectedDeptId.trim() || doctorsLoading"
              id="inputState"
              class="form-control form-box"
              v-model="selectedDoctor"
              ref="selectedDoctor"
            >
              <option value="">{{
                doctorsLoading ? 'Please wait' : 'Select Doctor'
              }}</option>
              <option
                v-for="{ sys: { id }, name } in doctorsList"
                :key="id"
                :value="name"
                >{{ name }}</option
              >
            </select>
          </div>

          <div class="col-lg-4 col-12 tab-responsive" id="date-picker">
            <!-- <v-date-picker  v-model="selectedDate" /> -->
            <v-date-picker
              :min-date="new Date()"
              v-model="selectedDate"
              ref="selectedDate"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <input
                  class="bg-white border px-2 py-1 rounded form-control form-box"
                  :value="inputValue"
                  v-on="inputEvents"
                  placeholder="Select a date"
                />
              </template>
            </v-date-picker>
          </div>
        </div>

        <p class="text-danger mt-3" style="text-align:center;" v-if="error">
          {{ error }}
        </p>

        <p class="text-success mt-3" style="text-align:center;" v-if="sent">
          appointment requested successfully.
        </p>

        <div class="form-row mt-3 ">
          <div class="col-lg-4 col-12 appointment-submit-btn">
            <button
              :disabled="sent || sending"
              type="submit"
              class="btn btn-primary btn-lg btn-block submit"
            >
              {{ sending ? 'Please wait' : 'Submit' }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<style>
.appointment-submit-btn {
  margin: auto;
}

.form-box {
  height: calc(1.5em + 0.75rem + 25px) !important;
}
</style>

<script lang="ts">
import { defineComponent } from 'vue';
import Facilities from '@/components/Appointment/Facilities.vue';
import { useQuery } from '@/hooks';
import {
  getDepartmentsList,
  getDoctorsListBasedOfDepartmentSelected,
} from '@/graphql';
import { GRAPHQL_QUERYING_HOSPITAL_NAMES } from '@/ts/enum';
import Loader from '@/components/LoadingAnimation.vue';
import {
  AppointmentDataInterface,
  DepartmentListPickTypeInterface,
  DoctorDataTypeInterface,
  MailSchemaInterface,
} from '@/ts/interfaces';
import { validateEmail } from '@/ts/functions';

export default defineComponent({
  data(): AppointmentDataInterface {
    return {
      departmentsList: [],
      loading: true,
      selectedDeptId: '',
      doctorsList: [],
      selectedDoctor: '',
      selectedDate: null,
      patientName: '',
      patientEmail: '',
      patientContactNumber: null,
      opNumber: null,
      patientMessage: '',
      error: null,
      sending: false,
      doctorsLoading: false,
      sent: false,
    };
  },
  components: {
    Facilities,
    Loader,
  },

  mounted() {
    (async () => {
      const { data, loading, errors } = await useQuery(getDepartmentsList, {
        hospital: GRAPHQL_QUERYING_HOSPITAL_NAMES.PROVIDENCE_HOSPITAL_ALAPPUZHA,
      });

      this.loading = loading;

      this.departmentsList = data
        .doctorsListingForParticularDepartmentCollection
        .items as DepartmentListPickTypeInterface[];
    })();
  },

  watch: {
    async selectedDeptId(curr, _next): Promise<void> {
      if (curr) {
        this.doctorsLoading = true;
        const { data, loading } = await useQuery(
          getDoctorsListBasedOfDepartmentSelected,
          {
            deptId: curr,
          }
        );

        this.doctorsLoading = loading;

        this.doctorsList = data.doctorsListingForParticularDepartment
          .doctorCollection.items as DoctorDataTypeInterface[];
      } else {
        this.selectedDoctor = '';
      }
    },
  },

  methods: {
    missingInputHandler(errMessage: string, ref: HTMLElement): void {
      this.error = errMessage;

      setTimeout(() => {
        this.error = null;
        ref.focus();
        ref.className = ref.className + ' is-invalid';
      }, 1000);

      setTimeout(() => {
        ref.className = ref.className.replace('is-invalid', '');
      }, 2000);
    },

    async sendMail() {
      if (
        !this.selectedDeptId &&
        this.$refs.selectedDeptId instanceof HTMLSelectElement
      ) {
        this.missingInputHandler(
          'Select a department',
          this.$refs.selectedDeptId
        );
        return;
      }

      // if (
      //   !this.selectedDoctor &&
      //   this.$refs.selectedDoctor instanceof HTMLSelectElement
      // ) {
      //   this.missingInputHandler("Select a doctor", this.$refs.selectedDoctor);
      //   return;
      // }

      // if (
      //   !this.selectedDate &&
      //   this.$refs.selectedDate instanceof HTMLElement
      // ) {
      //   this.missingInputHandler(
      //     "Select a date for appointment",
      //     this.$refs.selectedDate
      //   );
      //   return;
      // }

      if (
        !this.patientName &&
        this.$refs.patientName instanceof HTMLInputElement
      ) {
        this.missingInputHandler('Enter your name', this.$refs.patientName);
        return;
      }

      if (
        !this.patientEmail &&
        this.$refs.patientEmail instanceof HTMLInputElement
      ) {
        this.missingInputHandler(
          'Enter your email-address',
          this.$refs.patientEmail
        );
        return;
      }

      if (
        this.patientEmail &&
        !validateEmail(this.patientEmail) &&
        this.$refs.patientEmail instanceof HTMLInputElement
      ) {
        this.missingInputHandler(
          'Enter valid email-address',
          this.$refs.patientEmail
        );
        return;
      }

      if (
        !this.patientContactNumber &&
        this.$refs.patientContactNumber instanceof HTMLInputElement
      ) {
        this.missingInputHandler(
          'Enter your contact number',
          this.$refs.patientContactNumber
        );
        return;
      }

      if (
        this.patientContactNumber &&
        this.patientContactNumber.toString().length !== 10 &&
        this.$refs.patientContactNumber instanceof HTMLInputElement
      ) {
        this.missingInputHandler(
          'Enter valid contact number',
          this.$refs.patientContactNumber
        );
        return;
      }

      if (!process.env.VUE_APP_EMAIL_ONE) {
        return;
      }

      // AJAX
      const postDataModel: MailSchemaInterface = {
        patientName: this.patientName,
        doctor: this.selectedDoctor,
        department: this.departmentsList.filter(
          ({ sys: { id } }) => id === this.selectedDeptId
        )[0].departments,
        patientContactNumber: this.patientContactNumber,
        outPatientNumber: this.opNumber,
        message: this.patientMessage,
        appointmentOn: this.selectedDate,
        patientEmail: this.patientEmail,
        hospital: GRAPHQL_QUERYING_HOSPITAL_NAMES.PROVIDENCE_HOSPITAL_ALAPPUZHA,
      };

      try {
        this.sending = true;
        await this.axios.post(
          `${process.env.VUE_APP_BASE_URI}/api/v1/service/email`,
          { ...postDataModel, to: process.env.VUE_APP_EMAIL_ONE }
        );

        this.sending = false;
        this.sent = true;
      } catch (e) {
        this.sending = false;
        console.clear();
      }
    },
  },
});
</script>
